import React from 'react'
import { Globe, Facebook, Twitter } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface SEOPreviewsProps {
  title: string
  description: string
  url?: string
  image?: string
  siteName?: string
}

export const SEOPreviews: React.FC<SEOPreviewsProps> = ({
  title,
  description,
  url = 'https://example.com/blog/your-post',
  image,
  siteName = 'Joules Blog',
}) => {
  return (
    <div className="space-y-4">
      <GoogleSearchPreview
        title={title}
        description={description}
        url={url}
      />
      <FacebookCardPreview
        title={title}
        description={description}
        url={url}
        image={image}
        siteName={siteName}
      />
      <TwitterCardPreview
        title={title}
        description={description}
        image={image}
      />
    </div>
  )
}

interface GoogleSearchPreviewProps {
  title: string
  description: string
  url: string
}

export const GoogleSearchPreview: React.FC<GoogleSearchPreviewProps> = ({
  title,
  description,
  url,
}) => {
  // Truncate title to 60 chars for Google
  const displayTitle = title.length > 60 ? `${title.slice(0, 57)}...` : title
  // Truncate description to 160 chars
  const displayDescription = description.length > 160 ? `${description.slice(0, 157)}...` : description

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
          <Globe className="w-4 h-4" />
          <span>Google Search Preview</span>
        </div>
        <div className="space-y-1">
          <div className="text-sm text-green-700 dark:text-green-400">{url}</div>
          <h3 className="text-lg text-blue-800 dark:text-blue-400 hover:underline cursor-pointer">
            {displayTitle || 'Page Title'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {displayDescription || 'Page description will appear here...'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

interface FacebookCardPreviewProps {
  title: string
  description: string
  url: string
  image?: string
  siteName: string
}

export const FacebookCardPreview: React.FC<FacebookCardPreviewProps> = ({
  title,
  description,
  image,
  siteName,
}) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
          <Facebook className="w-4 h-4" />
          <span>Facebook Card Preview</span>
        </div>
        <div className="border border-border rounded-lg overflow-hidden">
          {image ? (
            <img
              src={image}
              alt="Preview"
              className="w-full h-52 object-cover"
            />
          ) : (
            <div className="w-full h-52 bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">Featured image will appear here</span>
            </div>
          )}
          <div className="p-3 bg-muted">
            <div className="text-xs text-muted-foreground uppercase mb-1">{siteName}</div>
            <h3 className="font-semibold text-sm mb-1 text-foreground">
              {title || 'Post Title'}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {description || 'Post description will appear here...'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface TwitterCardPreviewProps {
  title: string
  description: string
  image?: string
}

export const TwitterCardPreview: React.FC<TwitterCardPreviewProps> = ({
  title,
  description,
  image,
}) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
          <Twitter className="w-4 h-4" />
          <span>Twitter Card Preview</span>
        </div>
        <div className="border border-border rounded-2xl overflow-hidden">
          {image ? (
            <img
              src={image}
              alt="Preview"
              className="w-full h-48 object-cover"
            />
          ) : (
            <div className="w-full h-48 bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">Featured image will appear here</span>
            </div>
          )}
          <div className="p-3">
            <h3 className="font-semibold text-sm mb-1 text-foreground">
              {title || 'Post Title'}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {description || 'Post description will appear here...'}
            </p>
            <div className="text-xs text-muted-foreground mt-2">joulescrm.com</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}